/* =============================================================================
   VIEWS / ADMIN / ARCHIVE-BONS — Bons-Sub-Section (S68 REF-2)
   =============================================================================
   Extrahiert aus archive.js. Bon-Archiv = TSE-signierte Belege seit S57
   Auto-Archive (GoBD §147 AO + BSI TR-03153 + §146a AO). Quelle:
   Supabase-Tabelle `bon_archive` (Migration 70) via App.storage.bonArchive.

   Exports:
     - loadBons                Lazy-Hydrate des Bon-Archiv-Caches beim Tab-Öffnen
     - renderBonsFilters       Section-spezifische Filter-Leiste
     - getFilteredBons         Filter+Sort
     - renderBonsArchive       Sub-Section-Renderer
     - exportArchiveBonsCSV    CSV-Exporter
============================================================================ */

import { htmlEscape, fmtDate, showToast } from '../../utils/helpers.js';
import { _state } from './archive-state.js';
import { _renderImmutabilityNote, downloadCsv } from './archive-helpers.js';

/**
 * S213-A-HF — Bon-Archiv-Cache beim Öffnen des Bons-Tabs (lazy) auffrischen,
 * analog zu loadOrders. Der Cache wird zwar schon am Boot hydratisiert (für die
 * _ensureBonArchived-Idempotenz + Realtime), aber ein Refresh beim Tab-Öffnen
 * zieht Bons anderer Geräte nach, die seit dem Boot dazukamen (Multi-Device).
 * getFilteredBons liest synchron aus dem Cache → wir rendern sofort mit dem
 * vorhandenen Stand und ersetzen nach dem Refresh non-destruktiv nur die
 * Ergebnis-Liste (kein View-Hijack, kein Skeleton nötig).
 */
export async function loadBons() {
  _state.bonsLoading = true;
  try {
    const ba = window.App && window.App.storage && window.App.storage.bonArchive;
    if (ba && typeof ba.hydrate === 'function') await ba.hydrate();
    _state.bonsLoaded = true;
  } catch (e) {
    console.error('[archive] loadBons failed:', e);
  } finally {
    _state.bonsLoading = false;
    if (_state.section === 'bons' && typeof window.renderResultsOnly === 'function') {
      window.renderResultsOnly();
    }
  }
}

export function renderBonsFilters() {
  return `
    <div class="archive-filters">
      <div class="archive-filter-row">
        <label>
          <span>Zeitraum von</span>
          <input type="date" value="${_state.dateFrom}"
                 onchange="setArchiveFilter('dateFrom', this.value)">
        </label>
        <label>
          <span>bis</span>
          <input type="date" value="${_state.dateTo}"
                 onchange="setArchiveFilter('dateTo', this.value)">
        </label>
        <label class="archive-filter-search">
          <span>Suche</span>
          <input type="search" placeholder="Bon-Nr., Bestell-Nr., Name…"
                 value="${htmlEscape(_state.search)}"
                 oninput="setArchiveFilter('search', this.value)">
        </label>
      </div>
      <div class="archive-filter-row" style="justify-content:flex-end;">
        <button class="btn btn-ghost btn-sm" onclick="backfillBonArchive()"
                title="Legt zu jedem bezahlten Teilbeleg ohne Archiv-Zeile den Bon an. Mehrfach ausführbar — was schon da ist, bleibt unberührt.">
          🗄️ Fehlende Teilbeleg-Bons nachtragen
        </button>
      </div>
    </div>
  `;
}

/**
 * S248 — einmaliger Nachtrag fehlender Teilbeleg-Bons.
 *
 * Bis S248 archivierte der Bar-/Karten-Split nicht (live 0 von 17), obwohl das
 * Belegausgabe-Modal das Gegenteil sagte. Ab jetzt archiviert er beim Kassieren;
 * für den Bestand gibt es diesen Knopf.
 *
 * Bewusst eine ausdrückliche Aktion und kein stiller Boot-Lauf: es geht um das
 * Fiskal-Archiv, jede Zeile rendert einen Bon samt PDF, und wer nachträgt, soll
 * sehen, wie viele es waren. Idempotent — ein zweiter Lauf legt nichts an.
 */
export async function backfillBonArchive() {
  const ok = await window.styledConfirm({
    title: 'Fehlende Bons nachtragen?',
    body: 'Zu jedem bezahlten Teilbeleg ohne Archiv-Zeile wird der Bon erzeugt und '
        + 'abgelegt (mit Original-Datum und -Nummer). Es entstehen KEINE neuen '
        + 'TSE-Signaturen und keine Mails. Ein zweiter Lauf legt nichts an.',
    confirmLabel: 'Nachtragen'
  });
  if (!ok.confirmed) return;

  showToast('Bons werden nachgetragen …', 'info');
  try {
    const mod = await import('./stripe-bon-catchup.js');
    const n = await mod.backfillReceiptBons();
    const ba = window.App && window.App.storage && window.App.storage.bonArchive;
    if (ba && typeof ba.hydrate === 'function') await ba.hydrate();
    showToast(n > 0 ? `${n} Bon(s) nachgetragen.` : 'Nichts nachzutragen — alles archiviert.', 'success');
    if (typeof window.renderResultsOnly === 'function') window.renderResultsOnly();
  } catch (e) {
    console.error('[archive-bons] Nachtrag fehlgeschlagen:', e);
    showToast('Nachtrag fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

export function getFilteredBons() {
  const all = (typeof window.loadBonArchive === 'function') ? window.loadBonArchive() : {};
  const list = Object.values(all);
  const q = _state.search.trim().toLowerCase();
  const from = _state.dateFrom;
  const to   = _state.dateTo;

  let filtered = list.filter(b => {
    const d = (b.paidAt || b.archivedAt || '').slice(0, 10);
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  });

  if (q) {
    filtered = filtered.filter(b =>
      (b.invoiceNumber && String(b.invoiceNumber).toLowerCase().includes(q)) ||
      (b.orderNumber && String(b.orderNumber).toLowerCase().includes(q)) ||
      (b.customerName && b.customerName.toLowerCase().includes(q))
    );
  }

  filtered.sort((a, b) => (b.archivedAt || '').localeCompare(a.archivedAt || ''));
  return filtered;
}

export function renderBonsArchive() {
  const list = getFilteredBons();
  if (list.length === 0) {
    return `
      <div class="empty">
        <div class="empty-emoji">🗄️</div>
        Noch keine Bons im Archiv.
        <p style="font-size:.85rem; color: var(--ink-muted); margin-top: 8px;">
          Sobald eine Bestellung als «Bezahlt» markiert wird, landet der Bon automatisch hier (S57 Auto-Archive — GoBD §147 AO).
        </p>
      </div>
    `;
  }

  const cToE = (cents) => {
    const v = (Number(cents) || 0) / 100;
    return v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  };

  const pgB = (window.PaginatedList && window.PaginatedList.paginate)
    ? window.PaginatedList.paginate({
        items: list, containerId: 'archive-bons-pager',
        initial: 10, step: 10, rendererFn: 'renderArchive'
      })
    : { slice: list.slice(0, 10), moreButtonHtml: '', visible: Math.min(10, list.length), total: list.length };

  const rowsHtml = pgB.slice.map(b => {
    const id = htmlEscape(b.orderId);
    const corr = b.isCorrection ? '<span class="badge badge-declined">Stornobeleg</span>' : '';
    return `
      <tr class="archive-row" onclick="openOrderDetail('${id}')"
          title="Bestellungs-Detail öffnen">
        <td>
          ${b.invoiceNumber ? '<strong>' + htmlEscape(b.invoiceNumber) + '</strong>' : '<em style="color:var(--ink-muted);">— ohne —</em>'}
          ${corr}
          <br><span style="color: var(--ink-muted); font-size:.85em;">#${htmlEscape(b.orderNumber || b.orderId.slice(0, 6))}</span>
        </td>
        <td>${b.paidAt ? fmtDate(b.paidAt.slice(0, 10)) + '<br><span style="color: var(--ink-muted); font-size:.85em;">' + new Date(b.paidAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) + ' Uhr</span>' : '—'}</td>
        <td><strong>${htmlEscape(b.customerName || '—')}</strong></td>
        <td>${cToE(b.totalGrossCents)}</td>
        <td>${htmlEscape(b.paymentMethod || '—')}</td>
        <td><span style="color: var(--ink-muted); font-size:.85em;">${new Date(b.archivedAt).toLocaleString('de-DE')}</span></td>
        <td class="archive-bon-actions">
          <button class="btn btn-ghost btn-sm" title="Bon als PDF öffnen"
                  onclick="event.stopPropagation(); openArchivedBonPdf('${id}')">📄 PDF</button>
        </td>
      </tr>
    `;
  }).join('');

  return `
    <div class="archive-meta">
      <span>
        ${list.length} archivierte${list.length === 1 ? 'r' : ''} Bon${list.length === 1 ? '' : 's'}
        · zeige ${pgB.visible} von ${pgB.total}
      </span>
      <button class="btn-archive-reload" onclick="exportArchiveBonsCSV()"
              title="Bon-Archiv als CSV herunterladen"
              ${list.length === 0 ? 'disabled style="opacity:.45;cursor:not-allowed;"' : ''}>
        📥 CSV-Export
      </button>
    </div>
    <div class="archive-table-wrap">
      <table class="archive-table">
        <thead>
          <tr>
            <th>Bon-Nr. / Bestell-Nr.</th>
            <th>Bezahlt</th>
            <th>Kunde</th>
            <th>Brutto</th>
            <th>Bezahlart</th>
            <th>Archiviert</th>
            <th>Aktion</th>
          </tr>
        </thead>
        <tbody>${rowsHtml}</tbody>
      </table>
    </div>
    ${pgB.moreButtonHtml}
    ${_renderImmutabilityNote('BSI TR-03153 + §146a AO · TSE-signiert, append-only')}
  `;
}

export function exportArchiveBonsCSV() {
  const list = getFilteredBons();
  if (list.length === 0) { showToast('Keine Bons im Filter — nichts zu exportieren', 'info'); return; }

  const header = [
    'Bon-Nr.', 'Bestell-Nr.', 'Korrekturbeleg', 'Bezahlt am',
    'Kunde', 'E-Mail', 'Brutto (EUR)', 'MwSt 7% (EUR)', 'MwSt 19% (EUR)',
    'Bezahlart', 'Archiviert am'
  ];
  const cToE = (cents) => ((Number(cents) || 0) / 100).toFixed(2).replace('.', ',');
  const rows = [header];
  for (const b of list) {
    rows.push([
      b.invoiceNumber || '', b.orderNumber || '',
      b.isCorrection ? 'ja' : 'nein',
      b.paidAt || '',
      b.customerName || '', b.customerEmail || '',
      cToE(b.totalGrossCents), cToE(b.tax7Cents), cToE(b.tax19Cents),
      b.paymentMethod || '', b.archivedAt || ''
    ]);
  }
  downloadCsv(rows, `bons-archiv-${_state.dateFrom}_bis_${_state.dateTo}.csv`);
  showToast(`${list.length} Bon${list.length === 1 ? '' : 'en'} exportiert`, 'success');
}
